import { useState, useCallback } from 'react';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { exportEvaluationsCSV } from '@/features/evaluation/evaluation-export';

interface ExportPanelProps {
  reviewId: string;
  className?: string;
}

export function ExportPanel({ reviewId, className }: ExportPanelProps) {
  const [phase, setPhase] = useState<string>('all');
  const [bucket, setBucket] = useState<string>('all');
  const [isExporting, setIsExporting] = useState(false);
  const [previewCount, setPreviewCount] = useState<number | null>(null);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const { csv, count } = await exportEvaluationsCSV({
        reviewId,
        phase: phase === 'all' ? undefined : phase,
        bucket: bucket === 'all' ? undefined : bucket,
      });

      setPreviewCount(count);

      if (count === 0) {
        toast.warning('No records to export');
        return;
      }

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `review-export-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${count} records`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  }, [reviewId, phase, bucket]);

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-4 w-4 text-indigo-500" />
          <CardTitle className="text-base">Export Data</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Phase</Label>
            <Select value={phase} onValueChange={setPhase}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="All phases" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All phases</SelectItem>
                <SelectItem value="screening" className="text-xs">Screening</SelectItem>
                <SelectItem value="full_text" className="text-xs">Full Text</SelectItem>
                <SelectItem value="completed" className="text-xs">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-slate-500">Bucket</Label>
            <Select value={bucket} onValueChange={setBucket}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="All buckets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">All buckets</SelectItem>
                <SelectItem value="include" className="text-xs">Include</SelectItem>
                <SelectItem value="maybe" className="text-xs">Maybe</SelectItem>
                <SelectItem value="exclude" className="text-xs">Exclude</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Preview count */}
        {previewCount != null && (
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {previewCount} record{previewCount !== 1 ? 's' : ''}
            </Badge>
          </div>
        )}

        {/* Export button */}
        <Button
          className="w-full"
          onClick={handleExport}
          disabled={isExporting}
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-1.5" />
          )}
          Export CSV
        </Button>
      </CardContent>
    </Card>
  );
}
